import { isPublished } from '@shared/competitionRules';

export interface SeasonStandingEntry {
  competitorName: string;
  memberSub: string;
  totalPoints: number;
  competitionsCount: number;
  rank: number;
}

interface EventRecord {
  id: string;
  eventDate: string;
  publishedAt?: string | null;
  seniorChampionName?: string | null;
  seniorChampionPoints?: number | null;
  seniorChampionMemberSub?: string | null;
  juniorChampionName?: string | null;
  juniorChampionPoints?: number | null;
  juniorChampionMemberSub?: string | null;
}

export function computeSeasonStandings(
  events: EventRecord[],
  year: number,
): { seniorStandings: SeasonStandingEntry[]; juniorStandings: SeasonStandingEntry[] } {
  const seniorMap: Record<string, { name: string; points: number; count: number }> = {};
  const juniorMap: Record<string, { name: string; points: number; count: number }> = {};

  for (const evt of events) {
    if (!isPublished(evt)) continue;
    if (new Date(evt.eventDate).getFullYear() !== year) continue;

    if (evt.seniorChampionMemberSub && evt.seniorChampionPoints) {
      const sub = evt.seniorChampionMemberSub;
      if (!seniorMap[sub]) seniorMap[sub] = { name: evt.seniorChampionName || '', points: 0, count: 0 };
      seniorMap[sub].points += evt.seniorChampionPoints;
      seniorMap[sub].count += 1;
    }
    if (evt.juniorChampionMemberSub && evt.juniorChampionPoints) {
      const sub = evt.juniorChampionMemberSub;
      if (!juniorMap[sub]) juniorMap[sub] = { name: evt.juniorChampionName || '', points: 0, count: 0 };
      juniorMap[sub].points += evt.juniorChampionPoints;
      juniorMap[sub].count += 1;
    }
  }

  const toStandings = (map: typeof seniorMap): SeasonStandingEntry[] => {
    const entries = Object.entries(map)
      .map(([sub, d]) => ({ competitorName: d.name, memberSub: sub, totalPoints: d.points, competitionsCount: d.count, rank: 0 }))
      .sort((a, b) => b.totalPoints - a.totalPoints);
    // Dense ranking for ties
    let rank = 1;
    for (let i = 0; i < entries.length; i++) {
      if (i > 0 && entries[i].totalPoints < entries[i - 1].totalPoints) rank = i + 1;
      entries[i].rank = rank;
    }
    return entries;
  };

  return { seniorStandings: toStandings(seniorMap), juniorStandings: toStandings(juniorMap) };
}

export function findMemberRank(standings: SeasonStandingEntry[], memberSub: string): number | null {
  const entry = standings.find(s => s.memberSub === memberSub);
  return entry ? entry.rank : null;
}

export function findMemberPoints(standings: SeasonStandingEntry[], memberSub: string): number {
  const entry = standings.find(s => s.memberSub === memberSub);
  return entry ? entry.totalPoints : 0;
}
